import {useState, useEffect, useRef} from "react";
import {connect} from "react-redux";
import {Button, styled} from "@reside/ui";

import {SortDirection} from "../../store/reporting-reports/reporting-reports.common";
import {AdminAdmissionsTable} from "../../atoms/admin-admissions-table";
import {
  DashboardBoardScrollArea,
  DashboardBoardScrollShell,
} from "../../atoms/dashboard-table/DashboardBoardScroll";
import {DashboardBoardScrollMinimap} from "../../atoms/dashboard-table/DashboardBoardScrollMinimap";
import {useAdmissionsByStatusQuery} from "../../atoms/dashboard-table/hooks";
import {VIEW_MODE} from "../../atoms/dashboard-table/constants";
import {updateTableKey} from "../../atoms/list-table/helpers";
import {PageAdminAdmissionsProps, Status} from "./utils/types";
import {isResideAdmin} from "../../utils/helpers/users";
import {Spinner} from "../../atoms/spinner";
import {select, AppState} from "../../store";
import {LoadMoreWrapper} from "./styles";

/** Horizontal minimap segments (visual only; not tied to table column count). */
const ADMISSIONS_LIST_MINIMAP_TICKS = 6;

const ListBoardInner = styled.div`
  box-sizing: border-box;
  padding-left: var(--dashboard-h-pad);
  padding-right: var(--dashboard-h-pad);
  /* Wider than the viewport when columns don't fit — pairs with DashboardBoardScrollMinimap. */
  width: max-content;
  min-width: 100%;

  .lt-clip,
  .list-table {
    width: max-content;
    min-width: 100%;
  }
`;

/**
 * Page that will display all admissions and enable filtering by facilities and status.
 * If user wants to filter by facilites using dropdown @activeFacilitiesTreeKeys array is filled and used for filtering.
 * If logged in user is not allowed to see the dropdown by being allowed to see admissions belonging only to his facility,@hasSelectedFacility will check if that facility exists on array of facilities he is part of.
 * @isAllowedToSeeAdmissions prop will prevent unwanted users from seeing all admissions.
 *
 * */

type RendererProps = PageAdminAdmissionsProps & {
  admissionStatus: Status;
  selectItem: (item: Status) => void;
  starFilterActive?: boolean;
  workflowType?: string | null;
};

const Renderer = ({
  activeFacilitiesTreeKeys,
  hasSelectedFacilities,
  isAllowedToSeeAdmissions,
  admissionStatus,
  selectItem,
  starFilterActive,
  workflowType,
}: RendererProps) => {
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const [sortBy, setSortBy] = useState("dateOfAdmission");
  const [sortDirection, setSortDirection] = useState(SortDirection.ASC);

  const {
    data,
    hasNextPage,
    fetchNextPage,
    refetch,
    isLoading,
    isFetchingNextPage,
  } = useAdmissionsByStatusQuery({
    sortBy,
    sortDirection: sortDirection as any,
    status: admissionStatus.id,
    facilityIds: activeFacilitiesTreeKeys,
    pageSize: 30,
    isAllowedToSeeAdmissions,
    starredOnly: starFilterActive,
    viewMode: VIEW_MODE.LIST,
    workflowType,
  });

  // Refetch admissions when star/unstar happens and star filter is active
  useEffect(() => {
    const handleAdmissionStarToggled = () => {
      refetch();
    };

    window.addEventListener("admissionStarToggled", handleAdmissionStarToggled);
    return () => {
      window.removeEventListener(
        "admissionStarToggled",
        handleAdmissionStarToggled,
      );
    };
  }, [starFilterActive, activeFacilitiesTreeKeys, refetch]);

  const admissions = data?.pages.flatMap(item => item?.content);
  const [{totalElements = 0} = {}] = data?.pages ?? [];
  const displayedCount =
    hasSelectedFacilities && admissions ? admissions.length : 0;

  const onSort = (sortBy: string, sortDirection: SortDirection) => {
    setSortBy(sortBy);
    setSortDirection(sortDirection || SortDirection.NONE);
  };

  return (
    <DashboardBoardScrollShell>
      <DashboardBoardScrollArea ref={listScrollRef}>
        {isLoading ? (
          <Spinner />
        ) : (
          <ListBoardInner>
            <AdminAdmissionsTable
              admissions={
                hasSelectedFacilities && admissions ? (admissions as any) : []
              }
              onSort={onSort}
              sortBy={sortBy}
              sortDirection={sortDirection}
              tableName={updateTableKey(admissionStatus, "")}
              onAdmissionUpdated={() => {
                refetch();
              }}
              isAllowedToSeeAdmissions={isAllowedToSeeAdmissions}
            />
            {hasNextPage &&
              hasSelectedFacilities &&
              isAllowedToSeeAdmissions && (
                <LoadMoreWrapper>
                  <Button
                    size="medium"
                    color="primary"
                    disabled={
                      isFetchingNextPage ||
                      (activeFacilitiesTreeKeys &&
                        activeFacilitiesTreeKeys.length === 0)
                    }
                    onClick={() => fetchNextPage()}
                  >
                    {isFetchingNextPage ? "Loading..." : "Load More"}
                  </Button>
                  <span>{`${displayedCount} of ${
                    hasSelectedFacilities ? totalElements : 0
                  }`}</span>
                </LoadMoreWrapper>
              )}
          </ListBoardInner>
        )}
      </DashboardBoardScrollArea>
      {!isLoading && (
        <DashboardBoardScrollMinimap
          scrollRef={listScrollRef}
          columnCount={ADMISSIONS_LIST_MINIMAP_TICKS}
          tickLayout="horizontal"
        />
      )}
    </DashboardBoardScrollShell>
  );
};

const mapState = (state: AppState) => {
  const {facilities} = select.adminSession.user(state);

  return {
    activeFacilitiesTreeKeys:
      select.adminSession.activeFacilitiesTreeKeys(state),
    hasSelectedFacilities: select.adminSession.hasSelectedFacilities(state),
    isAllowedToSeeAdmissions:
      Boolean(facilities.length) ||
      isResideAdmin(select.adminSession.user(state)),
  };
};

export const DashboardAdmissionsListView = connect(mapState)(Renderer);
